import { ApiResponse } from '@shared/types/common';
import { EmailSummary, RelatedItem, SpoofDetectionResult } from '@shared/types/email';
import { Task, TaskExecution, TaskExecutionRequest } from '@shared/types/tasks';
import { UserPreferences } from '@shared/types/user';
import { API_BASE_URL } from '@shared/utils/constants';
import { getAccessToken, getUserIdFromToken, isSsoAvailable } from './authService';

/** Dashboard stats from API (stub shape may differ from DashboardStatistics) */
export interface DashboardStatsResponse {
  userId?: string;
  summariesGenerated?: number;
  linksOpened?: number;
  spoofChecks?: number;
  actionsExecuted?: number;
  periodStart?: string;
  periodEnd?: string;
  [key: string]: unknown;
}

class ApiService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  private async getAccessToken(): Promise<string> {
    if (!isSsoAvailable()) {
      throw new Error(
        'Sign-in is not available. Office SSO is only supported in Outlook with a work or school account. See docs/ENTRA_SSO_SETUP.md.'
      );
    }
    return getAccessToken();
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const token = await this.getAccessToken();
    const url = `${this.baseUrl}${endpoint}`;

    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const body = await response.json().catch(() => ({})) as { error?: { message?: string }; message?: string };
      const message = body.error?.message ?? body.message ?? response.statusText ?? `HTTP ${response.status}`;
      throw new Error(message);
    }

    return response.json();
  }

  async getEmailSummary(emailId: string, preferences?: { length?: string; language?: string }): Promise<EmailSummary> {
    const token = await this.getAccessToken();
    const user = getUserIdFromToken(token);
    const response = await this.request<EmailSummary>('/summaries', {
      method: 'POST',
      headers: user?.oid
        ? { 'X-User-Id': user.oid, ...(user.email && { 'X-User-Email': user.email }) }
        : undefined,
      body: JSON.stringify({
        emailId,
        preferences: preferences || { length: 'medium', language: 'en' },
        userId: user?.oid ?? undefined,
        userEmail: user?.email ?? undefined,
      }),
    });

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to get email summary');
    }

    return response.data;
  }

  async getRelatedLinks(emailId: string, searchScope?: {
    includeEmails?: boolean;
    includeDocuments?: boolean;
    maxResults?: number;
  }): Promise<{ relatedItems?: RelatedItem[] }> {
    const response = await this.request<{ relatedItems?: RelatedItem[] }>('/links/related', {
      method: 'POST',
      body: JSON.stringify({
        emailId,
        searchScope: searchScope || { includeEmails: true, includeDocuments: true, maxResults: 10 },
      }),
    });

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to get related links');
    }

    return response.data;
  }

  async detectSpoof(emailId: string): Promise<SpoofDetectionResult> {
    const response = await this.request<SpoofDetectionResult>('/spoof/detect', {
      method: 'POST',
      body: JSON.stringify({ emailId }),
    });

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to detect spoof');
    }

    return response.data;
  }

  async executeTask(request: TaskExecutionRequest): Promise<TaskExecution> {
    const response = await this.request<TaskExecution>('/tasks/execute', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to execute task');
    }

    return response.data;
  }

  async getAvailableTasks(): Promise<Task[]> {
    const response = await this.request<Task[]>('/tasks/available', {
      method: 'GET',
    });

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to get available tasks');
    }

    return response.data;
  }

  async getUserPreferences(userId: string): Promise<UserPreferences> {
    const response = await this.request<UserPreferences>(`/users/${userId}/preferences`, {
      method: 'GET',
    });

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to get user preferences');
    }

    return response.data;
  }

  async updateUserPreferences(userId: string, preferences: Partial<Pick<UserPreferences, 'featureToggles' | 'summaryLength' | 'taskPaneConfig' | 'automationRules'>>): Promise<void> {
    const response = await this.request<unknown>(`/users/${userId}/preferences`, {
      method: 'PUT',
      body: JSON.stringify(preferences),
    });

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to update user preferences');
    }
  }

  async getDashboardStatistics(userId: string, startDate?: string, endDate?: string): Promise<DashboardStatsResponse> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const queryString = params.toString();
    const endpoint = `/users/${userId}/dashboard${queryString ? `?${queryString}` : ''}`;

    const response = await this.request<DashboardStatsResponse>(endpoint, {
      method: 'GET',
    });

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to get dashboard statistics');
    }

    return response.data;
  }
}

export const apiService = new ApiService();





